<script setup lang="ts">
  import { ref } from 'vue';
  import { Clipboard, Copy } from 'lucide-vue-next';
  import { storeToRefs } from 'pinia';
  import { useModal } from 'vue-final-modal';
  import EraserIcon from '@/shared/assets/icons/EraserIcon.svg';
  import { Accordion } from '@/shared/ui';
  import ConfirmDeleteModal from '@/shared/ui/modals/ConfirmDeleteModal.vue';
  import { useFormBuilderToast } from '../../composables/useFormBuilderToast';
  import { usePermissionSubjectActions } from '../../composables/usePermissionSubjectActions';
  import { usePermissionsClipboard } from '../../composables/usePermissionsClipboard';
  import { useFormPermissionsSelectionStore } from '../../model/form-permissions-selection.store';
  import PermissionsAddSection from './PermissionsAddSection.vue';
  import PermissionsClipboardButton from './PermissionsClipboardButton.vue';
  import PermissionsSelectedFieldsBar from './PermissionsSelectedFieldsBar.vue';
  import PermissionSubjectAccordion from './PermissionSubjectAccordion.vue';
  import type { PermissionSubject } from '../../composables/usePermissionSubjects';

  withDefaults(
    defineProps<{
      positions: PermissionSubject[];
      functionalActivities: PermissionSubject[];
      isLoadingPositions?: boolean;
      isLoadingFunctionalActivities?: boolean;
      hasPositionsError?: boolean;
      hasFunctionalActivitiesError?: boolean;
      locked?: boolean;
    }>(),
    { locked: false }
  );

  const selectionStore = useFormPermissionsSelectionStore();
  const { addMode, items: selectedItems } = storeToRefs(selectionStore);
  const { showSuccessToast } = useFormBuilderToast();
  const clipboard = usePermissionsClipboard();

  function openClearSelectedModal() {
    if (selectedItems.value.length === 0) return;

    const { open, close } = useModal({
      component: ConfirmDeleteModal,
      attrs: {
        icon: EraserIcon,
        iconAlt: 'Limpar selecionados',
        title: 'Tem certeza que deseja limpar todos os Campos selecionados?',
        description: 'A ação não pode ser desfeita e as edições feitas serão perdidas.',
        onClose() {
          close();
        },
        onConfirm() {
          close();
          selectionStore.clearItems();
          showSuccessToast({ title: 'Todos os Campos selecionados foram limpos', type: 'success' });
        },
      },
    });

    open();
  }

  const openSections = ref(['positions', 'functional-activities']);
  const positionActions = usePermissionSubjectActions('positions');
  const {
    selectedIds: selectedPositionIds,
    isSectionDisabled: isPositionsDisabled,
    isApplyDisabled: isPositionsApplyDisabled,
    isClearDisabled: isPositionsClearDisabled,
  } = positionActions;
  const functionalActivityActions = usePermissionSubjectActions('functionalActivities');
  const {
    selectedIds: selectedFunctionalActivityIds,
    isSectionDisabled: isFunctionalActivitiesDisabled,
    isApplyDisabled: isFunctionalActivitiesApplyDisabled,
    isClearDisabled: isFunctionalActivitiesClearDisabled,
  } = functionalActivityActions;
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col" data-testid="permissions-configure-tab">
    <div class="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-6">
      <div class="flex flex-col gap-6 rounded-lg border border-gray-02 p-4">
        <PermissionsAddSection :disabled="locked" />
        <PermissionsSelectedFieldsBar
          :fields="selectedItems"
          :locked="locked || addMode"
          :readonly="locked"
          @remove="selectionStore.removeItem"
          @clear="openClearSelectedModal"
        />

        <Accordion
          v-model="openSections"
          type="multiple"
          class="flex flex-col gap-6"
          :inert="addMode"
          :class="{ 'opacity-60': addMode }"
        >
          <PermissionSubjectAccordion
            v-model:selected-ids="selectedPositionIds"
            mode="positions"
            :items="positions"
            :loading="isLoadingPositions"
            :has-error="hasPositionsError"
            :disabled="locked || isPositionsDisabled"
            :apply-disabled="isPositionsApplyDisabled"
            :clear-disabled="isPositionsClearDisabled"
            @apply="positionActions.apply"
            @clear="positionActions.clear"
          />

          <PermissionSubjectAccordion
            v-model:selected-ids="selectedFunctionalActivityIds"
            mode="functionalActivities"
            :items="functionalActivities"
            :loading="isLoadingFunctionalActivities"
            :has-error="hasFunctionalActivitiesError"
            :disabled="locked || isFunctionalActivitiesDisabled"
            :apply-disabled="isFunctionalActivitiesApplyDisabled"
            :clear-disabled="isFunctionalActivitiesClearDisabled"
            @apply="functionalActivityActions.apply"
            @clear="functionalActivityActions.clear"
          />
        </Accordion>
      </div>
    </div>

    <div
      class="flex shrink-0 gap-4 border-t border-gray-02 bg-white-02 px-4 py-4"
      :inert="addMode"
      data-testid="permissions-clipboard-bar"
    >
      <PermissionsClipboardButton
        text="Copiar permissões"
        :icon="Copy"
        :disabled="!clipboard.canCopy.value"
        data-testid="permissions-copy-btn"
        @click="clipboard.copy"
      />
      <PermissionsClipboardButton
        text="Colar permissões"
        :icon="Clipboard"
        :disabled="!clipboard.canPaste.value"
        data-testid="permissions-paste-btn"
        @click="clipboard.paste"
      />
    </div>
  </div>
</template>
